import React, { useState, useEffect } from 'react'
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { useTranslation } from 'react-i18next'
import { supabase } from '../supabaseClient'

export default function TimeZoneSelector({ value, onChange, disabled = false }) {
  const { t } = useTranslation()
  const [currentTime, setCurrentTime] = useState('')
  const [timezones, setTimezones] = useState([])
  const [loading, setLoading] = useState(true)

  // Fetch timezones from the database
  useEffect(() => {
    const fetchTimezones = async () => {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('platform_timezones')
          .select('id, code, label')
          .order('label', { ascending: true })

        if (error) {
          throw error
        }

        setTimezones(data || [])
      } catch (error) {
        console.error('Error fetching timezones:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchTimezones()
  }, [supabase])

  // Update the current time based on selected timezone
  useEffect(() => {
    if (!value) return

    const updateCurrentTime = () => {
      try {
        // Find the timezone code from the selected value (which is now the ID)
        const selectedTimezone = timezones.find((tz) => tz.id === value)

        if (!selectedTimezone) {
          setCurrentTime('--:--:--')
          return
        }

        const options = {
          timeZone: selectedTimezone.code,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }

        const formatter = new Intl.DateTimeFormat('en-US', options)
        setCurrentTime(formatter.format(new Date()))
      } catch (error) {
        console.error('Error formatting time with timezone:', error)
        setCurrentTime('--:--:--')
      }
    }

    if (timezones.length > 0) {
      updateCurrentTime()
      const intervalId = setInterval(updateCurrentTime, 1000)
      return () => clearInterval(intervalId)
    }
  }, [value, timezones])

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <AccessTimeIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="subtitle1" fontWeight="medium">
          {t('settings.timezone.title', 'Time Zone')}
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t(
          'settings.timezone.description',
          'Select your time zone for scheduling viewings and appointments.'
        )}
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        {t(
          'settings.timezone.info',
          "Your time zone is used to display appointment times correctly. We'll convert times between you and the property agents automatically."
        )}
      </Alert>

      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <FormControl sx={{ minWidth: 240 }}>
          <InputLabel id="timezone-select-label">
            {t('settings.timezone.select', 'Select Time Zone')}
          </InputLabel>
          {loading ? (
            <CircularProgress size={24} sx={{ ml: 1, mt: 2 }} />
          ) : (
            <Select
              labelId="timezone-select-label"
              value={value || ''}
              onChange={onChange}
              label={t('settings.timezone.select', 'Select Time Zone')}
              disabled={disabled || loading}
            >
              {timezones.map((timezone) => (
                <MenuItem key={timezone.id} value={timezone.id}>
                  {timezone.label}
                </MenuItem>
              ))}
            </Select>
          )}
        </FormControl>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: 'background.paper',
            px: 2,
            py: 1,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            {t('settings.timezone.currentTime', 'Current time:')}
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            {currentTime}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}
